import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {ListResponse} from '../interface/response/list-response';
import {HttpClient} from '@angular/common/http';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {Uuid} from '../interface/response/uuid';
import {first, take} from 'rxjs/operators';
import {Department} from '../interface/department';
import {User} from '../interface/user';
import {Dictionary} from "../interface/dictionary";

@Injectable({
  providedIn: 'root'
})
export class DepartmentService {
  loaded = false;
  private readonly departmentListSubject: ReplaySubject<CountedAndPaginated<Department>>
    = new ReplaySubject<CountedAndPaginated<Department>>(1);
  private readonly departmentNotCountedListSubject: ReplaySubject<ListResponse<Department>>
    = new ReplaySubject<ListResponse<Department>>(1);
  private readonly employeeListSubject: ReplaySubject<ListResponse<User>>
    = new ReplaySubject<ListResponse<User>>(1);
  private readonly departmentSubject: ReplaySubject<Array<Department>> = new ReplaySubject();


  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public sendForm(formData: object, departmentId?: string): Observable<Uuid> {
    if (departmentId) {
      return this.httpClient.put(`/rest/sso/api/department/${departmentId}`, formData)
        .pipe(
          take(1)
        ) as Observable<Uuid>;
    } else {
      return this.httpClient.post('/rest/sso/api/department', formData)
        .pipe(
          take(1)
        ) as Observable<Uuid>;
    }
  }

  public get(departmentId: string): Observable<Department> {
    return this.httpClient.get(`/rest/sso/api/department/${departmentId}`)
      .pipe(first()) as Observable<Department>;
  }

  public load(): void {
    this.httpClient.get('/rest/sso/api/department?disableCount=1&page=1&pageSize=999&')
      .pipe(first())
      .subscribe((response: ListResponse<Department>) => {
        this.departmentSubject.next(response.items);
        this.loaded = true;
      });
  }

  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/sso/api/department?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Department>) => {
        this.departmentListSubject.next(result);
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object): void {
    this.httpClient
      .get(`/rest/sso/api/department?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Department>) => {
        this.departmentNotCountedListSubject.next(result);
      });
  }

  public getDepartmentListSubject(): ReplaySubject<CountedAndPaginated<Department>> {
    return this.departmentListSubject;
  }

  public getDepartmentNotCountedListSubject(): ReplaySubject<ListResponse<Department>> {
    return this.departmentNotCountedListSubject;
  }

  public downloadReport(filters: object): void {
    this.document.location.href = `/rest/sso/api/department/method/report?` + this.filterService.parseFilters(filters);
  }

  public getEmployeeListSubject(): ReplaySubject<ListResponse<User>> {
    return this.employeeListSubject;
  }

  public loadEmployeeList(departmentId: string): void {
    this.httpClient.get(`/rest/sso/api/department/${departmentId}/employee`)
      .pipe(first()).subscribe((response: ListResponse<User>) => {
        this.employeeListSubject.next(response);
    });
  }

  updateSupervisorPermissions(departmentId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/department/${departmentId}/supervisorPermissions`, formData)
      .pipe(first());
  }

  updateEmployeePermissions(departmentId: string, formData: any): Observable<any> {
    return this.httpClient.put(`/rest/sso/api/department/${departmentId}/employeePermissions`, formData)
      .pipe(first());
  }

  updateApplicationPermissions(
    departmentId: string,
    assignmentType: 'employee' | 'supervisor',
    formData: any
  ): Observable<any> {
    return this.httpClient.put(
      `/rest/sso/api/department/${departmentId}/applicationPermissions/${assignmentType}`,
      {
        applicationPermissions: formData
      }
    ).pipe(first());
  }

  public getDepartment(name: string): Observable<string> {
    this.loadIfNotLoaded();
    const departObservable: ReplaySubject<string> = new ReplaySubject<string>(1);

    this.departmentSubject.pipe(first())
      .subscribe((items: Department[]) => {
        items.some((el: Department) => {
          if (el.name === name) {
            departObservable.next(el.uuid);
            departObservable.complete();
            return true;
          }

          return false;
        });

        departObservable.complete();
      });

    return departObservable.pipe(first());
  }

  private loadIfNotLoaded(): void {
    if (!this.loaded) {
      this.load();
    }
  }
}
